import {
  Car,
  CarsByVin,
  CarType,
  CarTypeResponse,
  Manufacture,
  ManufacturersResponse,
  OtherCar,
  SelectedCarCookie,
  Serie,
  SerieResponse,
} from "@/cars/types";
import { ActionContext, Module } from "vuex";
import axios, { AxiosResponse } from "axios";
import { State } from "@/store";
import Cookies from "js-cookie";
import { Optional } from "@/globalTypes";

interface CarState {
  isCarsLoading: Boolean;
  cars: Array<Car>;
  selectedCar: Optional<Car>;
  manufacturers: Array<Manufacture>;
  limit: Number;
}

function setSelectedCarInCookie(car: Optional<Car>) {
  if (car) {
    Cookies.set(
      "selectedCar",
      JSON.stringify({ vin: car.vin, typeId: car.typeId })
    );
  } else {
    Cookies.remove("selectedCar");
  }
}

function saveTmpCarInCookie(car: Optional<Car>) {
  if (car) {
    Cookies.set("selectedCar", JSON.stringify(car));
  } else {
    Cookies.remove("selectedCar");
  }
}

export const store: Module<CarState, State> = {
  namespaced: true,
  state: () => ({
    isCarsLoading: false,
    cars: [],
    selectedCar: null,
    manufacturers: [],
    limit: 2,
  }),
  mutations: {
    addCar(state: CarState, car: Car) {
      state.cars.push(car);
    },
    setSelectedCar(state: CarState, car: Optional<Car>) {
      setSelectedCarInCookie(car);
      state.selectedCar = car;
    },
  },
  getters: {
    getCars(state: CarState): Array<Car> {
      return state.cars;
    },
    getSelectedCar(state: CarState): Optional<Car> {
      return state.selectedCar;
    },
    getManufacturers(state: CarState): Array<Manufacture> {
      return state.manufacturers;
    },
    getLimit(state: CarState): Number {
      return state.limit;
    },
    isCarsLoading(state: CarState): Boolean {
      return state.isCarsLoading;
    },
  },
  actions: {
    createCar(context: ActionContext<CarState, State>, car: Car): Promise<Car> {
      return new Promise((resolve) => {
        axios
          .post(`/api/cars/`, car, {
            headers: { "X-CSRFTOKEN": context.rootGetters["accounts/csrf"] },
          })
          .then(() => {
            setSelectedCarInCookie(car);
            context.commit("addCar", car);
            context.commit("setSelectedCar", car);
            resolve(car);
          });
      });
    },
    createTmpCar(
      context: ActionContext<CarState, State>,
      user_id: string
    ): Promise<Optional<Car>> {
      return new Promise((resolve) => {
        const tmpCar = Cookies.get("selectedCar");
        Cookies.remove("selectedCar");
        if (tmpCar) {
          const car = JSON.parse(tmpCar);
          car["user"] = user_id;
          context.dispatch("createCar", car).then((savedCar) => {
            resolve(savedCar);
          });
        } else {
          resolve(null);
        }
      });
    },
    getByVin(
      context: ActionContext<CarState, State>,
      vin: String
    ): Promise<AxiosResponse<CarsByVin>> {
      return axios.get(`/api/tec/vin/${vin}/`);
    },
    preCheckVin(
      context: ActionContext<CarState, State>,
      vin: String
    ): Promise<AxiosResponse<CarsByVin>> {
      return axios.post(
        "/vin/audatex/api/vin/pre-check/",
        { vin: vin },
        {
          headers: { "X-CSRFTOKEN": context.rootGetters["accounts/csrf"] },
        }
      );
    },
    decodeVin(
      context: ActionContext<CarState, State>,
      vin: String
    ): Promise<AxiosResponse<CarsByVin>> {
      return axios.post(
        "/vin/audatex/api/vin/",
        { vin: vin },
        {
          headers: { "X-CSRFTOKEN": context.rootGetters["accounts/csrf"] },
        }
      );
    },
    pullCars(context: ActionContext<CarState, State>): Promise<any> {
      context.state.isCarsLoading = true;
      return axios
        .get(`/api/cars/`)
        .then((response: AxiosResponse<Array<Car>>) => {
          const cars = response.data;
          context.state.cars = cars;
          const selectedCarCookie = Cookies.get("selectedCar");
          if (selectedCarCookie) {
            const parsedSelectedCarCookie = JSON.parse(
              selectedCarCookie
            ) as SelectedCarCookie;
            const selectedCar = response.data.find((car) =>
              parsedSelectedCarCookie.vin
                ? car.vin === parsedSelectedCarCookie.vin
                : car.typeId === parsedSelectedCarCookie.typeId
            );
            context.commit("setSelectedCar", selectedCar || cars[0]);
          } else {
            context.commit("setSelectedCar", cars[0]);
          }
          context.state.isCarsLoading = false;
        })
        .catch(() => {
          context.state.cars = [];
          context.commit("setSelectedCar", null);
          context.state.isCarsLoading = false;
        });
    },
    pullManufacturers(context: ActionContext<CarState, State>): void {
      axios
        .get("/api/tec/manufacturers/")
        .then((response: AxiosResponse<ManufacturersResponse>) => {
          context.state.manufacturers = response.data.manufacturers;
        });
    },
    pullSeries(
      context: ActionContext<CarState, State>,
      manufactureId: Number
    ): Promise<Array<Serie>> {
      return new Promise((resolve) => {
        axios
          .get(`/api/tec/manufacturers/${manufactureId}/series/`)
          .then((response: AxiosResponse<SerieResponse>) => {
            resolve(response.data.series);
          });
      });
    },
    pullTypes(
      context: ActionContext<CarState, State>,
      { manufactureId, serieId }: { manufactureId: Number; serieId: Number }
    ): Promise<Array<CarType>> {
      return new Promise((resolve) => {
        axios
          .get(
            `/api/tec/manufacturers/${manufactureId}/series/${serieId}/types/`
          )
          .then((response: AxiosResponse<CarTypeResponse>) => {
            resolve(response.data.types);
          });
      });
    },
    getCarByTypeId(
      context: ActionContext<CarState, State>,
      typeId: Number
    ): Promise<OtherCar> {
      return new Promise((resolve) => {
        axios
          .get(`/api/tec/type_details/${typeId}/`)
          .then((response: AxiosResponse<OtherCar>) => {
            resolve(response.data);
          });
      });
    },
    saveTmpCar(context: ActionContext<CarState, State>, car: Car): void {
      saveTmpCarInCookie(car);
    },
  },
};
